import { Controller } from "stimulus";
import { Formio } from "formiojs";

import Hammer from "hammerjs";
import * as cornerstone from "cornerstone-core";
import cornerstoneTools from "cornerstone-tools";
import cornerstoneMath from "cornerstone-math";
import dicomParser from "dicom-parser";
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";

import * as cornerstoneWebImageLoader from "cornerstone-web-image-loader";
import BaseFileModule from "./shared_modules/base_file_module.js";
import FormStateModule from "./shared_modules/form_state_module.js";
import NotificationModule from "./shared_modules/notification_module.js";
import FormSubmissionModule from "./shared_modules/form_submission_module.js";
import FormAuditHistoryModule, {
  buildAuditHistoryJsonUrl,
  fetchAuditHistory,
  buildAuditHistoryPrintHtml,
  buildChangeSummaryPrintHtml,
  shouldShowAuditHistory,
} from "./shared_modules/form_audit_history_module.js";

cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
cornerstoneWADOImageLoader.configure({
  beforeSend: function (xhr) {
    // Add custom headers here (e.g. auth tokens)
    //xhr.setRequestHeader('x-auth-token', 'my auth token');
  },
  useWebWorkers: true,
});

const {
  PanTool,
  WwwcTool,
  ZoomMouseWheelTool,
  LengthTool,
  ArrowAnnotateTool,
  AngleTool,
  EllipticalRoiTool,
  EraserTool,
  RectangleRoiTool,
  FreehandRoiTool,
  StackScrollMouseWheelTool,
} = cornerstoneTools;

const LEFT_CLICK = 1;
const MIDDLE_CLICK = 4;
// const RIGHT_CLICK = 2;

export default class extends Controller {
  static targets = [
    "viewer",
    "url",
    "form",
    "path",
    "agreement",
    "imageTag",
    "submit",
    "update",
    "menu",
    "cornerstone",
    "formSection",
    "menuIcon",
    "cornerstoneIcon",
    "questionArea",
    "loadingImage",
    "submitButton",
    "auditHistory",
    "changeSummary",
  ];

  connect() {
    this.isSubmitting = false;
  }

  async initialize() {
    // Define Targets
    const elem = this.viewerTarget;
    this.imagesMenu = this.menuTarget;
    this.cornerstoneImages = this.cornerstoneTarget;
    this.formSection = this.formSectionTarget;
    this.EligibilityFormUserId = this.data.get("userId");
    this.eligibilityFormElem = this.formTarget;
    this.EligibilityFormAgreementElem = this.agreementTarget;
    this.imageTag = this.imageTagTarget;
    this.gradingFormStackId = this.data.get("stackId");
    this.imageTagList = [
      { label: "Study Date", hex: "x00080020" },
      { label: "Study Time", hex: "x00080030" },
      { label: "Operators Name", hex: "x00081070" },
      { label: "Manufacturer's Model Name", hex: "x00081090" },
      { label: "Patient ID", hex: "x00100020" },
      { label: "Device Serial Number", hex: "x00181000" },
      { label: "Software Version(s)", hex: "x00181020" },
      { label: "Image Laterality", hex: "x00200062" },
    ];
    this.viewerElement = elem;
    const imageKeys = this.data.get("imageKeys");
    this.eligibilityAnnotationSaveUrl =
      document.location.origin + this.data.get("updatePath") + ".json";
    if (imageKeys && imageKeys.trim() !== "[]") {
      // Split the string by commas and remove any leading/trailing spaces, then filter out empty strings
      this.gradingImages = imageKeys
        .split(",")
        .map((key) => key.trim())
        .filter(Boolean);
    } else {
      // If imageKeys is not provided or is empty/contains only [], set this.gradingImages to an empty array
      this.gradingImages = [];
    }
    this.images = [];
    this.readImages();
    this.currentImage = 0;
    this.eligibilityFormVersionId = this.data.get("versionId");
    // load previously saved grading data if available
    this.annotation = JSON.parse(this.data.get("annotation"));
    this.annotationIsLoadedOnce = {};
    this.eligibilityFormSchema = JSON.parse(this.data.get("eligibilityForm"));
    this.eligibilityFormResponses = JSON.parse(this.data.get("responses"));
    this.eligibilitySaveUrl =
      document.location.origin +
      this.data.get("eligibilityFormUpdatePath") +
      ".json";
    this.eligibilitystudyId = this.data.get("studyId");
    // Read print preferences
    this.printFormWithData = (() => {
      const value =
        this.data.get("printFormWithData") ||
        this.data.get("print-form-with-data");
      return value === "true" || value === true || value === "1" || value === 1;
    })();
    this.includeAuditSummaryInPdf = (() => {
      const value = this.data.get("includeAuditSummaryInPdf");
      return value === "true" || value === true || value === "1" || value === 1;
    })();
    this.includeChangeSummaryInPdf = (() => {
      const value = this.data.get("includeChangeSummaryInPdf");
      return value === "true" || value === true || value === "1" || value === 1;
    })();
    // Store original schema for printing empty form
    this.originalEligibilityFormSchema = JSON.parse(
      this.data.get("eligibilityForm"),
    );
    // Sample URL
    const url = this.eligibilitySaveUrl;
    // Split the URL by '/'
    const urlParts = url.split("/");
    // Extract the stack id from the URL
    const stack = urlParts[urlParts.indexOf("stacks") + 1];
    this.stackId = stack;

    // renderer
    this.eligibilityForm = await Formio.createForm(
      this.eligibilityFormElem,
      this.eligibilityFormSchema,
      {
        noAlerts: true,
        readOnly:
          this.data.get("readOnly") === "true" ||
          new URLSearchParams(window.location.search).get("page_type") ===
            "view_only",
      },
    );

    // set the draft responses if any
    this.eligibilityForm.submission = this.eligibilityFormResponses;
    this.formSaveDraftStatus = false;

    const displayImagesForQuestions = (
      formComponents,
      eligibilityFormResponses,
      ImagesArray,
    ) => {
      // Split the string into an array of URLs using a comma as the separator
      const imagesArray = ImagesArray.split("<==>").map((url) => url.trim());
      // Iterate through the form components
      FormioUtils.eachComponent(formComponents, (component) => {
        // Check if the component key is in eligibilityFormResponses
        if (component.key in eligibilityFormResponses.data) {
          const questionKey = component.key;
          const verified_key = questionKey + "_sdv_verified";
          const comments_key = questionKey + "_sdv_comments";
          if (eligibilityFormResponses.sdv) {
            if (verified_key in eligibilityFormResponses.sdv) {
              const check_sdv = eligibilityFormResponses.sdv[verified_key];
              const sdv_comments = eligibilityFormResponses.sdv[comments_key];
              this.appendSdvWithQuestionTitles(
                component,
                check_sdv,
                sdv_comments,
              );
            }
          }
          if (
            eligibilityFormResponses &&
            eligibilityFormResponses.files &&
            questionKey in eligibilityFormResponses.files
          ) {
            const imageUrls = eligibilityFormResponses.files[questionKey];
            this.appendImageWithQuestion(component, imageUrls, imagesArray);
          }
        }
      });
    };

    // Call the function with your data
    Formio.createForm(
      this.eligibilityFormElem,
      "https://examples.form.io/example",
    ).then((form) => {
      this.eligibilityForm.on("render", () => {
        if (Object.keys(this.eligibilityFormResponses).length > 0) {
          displayImagesForQuestions(
            this.eligibilityForm.components,
            this.eligibilityFormResponses,
            this.data.get("attachments"),
          );
        }
      });
      // uncomment the line below to rerender
      this.eligibilityForm.redraw();
      if (this.eligibilityForm.disabled) {
        const dropzoneElements = document.getElementsByClassName("dropzone");
        for (const element of dropzoneElements) {
          element.classList.add("hidden");
        }
      }
    });
    /**
     *
     * Corner Stone Setup
     */
    cornerstoneWebImageLoader.external.cornerstone = cornerstone;
    cornerstoneTools.external.cornerstone = cornerstone;
    cornerstoneTools.external.Hammer = Hammer;
    cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
    cornerstoneTools.init({
      mouseEnabled: true,
      touchEnabled: true,
      globalToolSyncEnabled: true,
      showSVGCursors: true,
    });

    cornerstoneTools.loadHandlerManager.setErrorLoadingHandler(
      (element, imageId, error) => {
        console.error(imageId);
        // throw error;
      },
    );

    cornerstone.enable(elem);
    this.imageLoader(this.url);
    // attach event listeners
    this.viewerElement.addEventListener("cornerstonenewimage", (e) => {
      // By resetting viewer the tooldata is not affected
      cornerstone.reset(this.viewerElement);
      e.detail.viewport.displayedArea.brhc.x = e.detail.image.width;
      e.detail.viewport.displayedArea.brhc.y = e.detail.image.height;
    });
    await this.checkifAllImagesUploaded();

    // Initialize shared modules
    this.baseFile = new BaseFileModule(this.element);
    this.formState = new FormStateModule(
      this.eligibilityForm,
      this.formTarget,
      this.hasSubmitTarget ? this.submitTarget : null,
      this.hasUpdateTarget ? this.updateTarget : null,
    );
    this.notifications = new NotificationModule(
      this.hasLoadingImageTarget ? this.loadingImageTarget : null,
    );
    this.notifications.hideLoader();
    this.formSubmissionModule = new FormSubmissionModule({
      formSaveUrl:
        document.location.origin +
        this.data.get("eligibilityFormUpdatePath") +
        ".json",
      formVersionId: this.data.get("versionId"),
      formUserId: this.data.get("userId"),
      formStackId: this.data.has("stackId") ? this.data.get("stackId") : null,
      notifications: this.notifications,
      stackIsFinalUrl: "",
      images: this.gradingImages,
    });
    // Set the form in modules
    this.baseFile.setForm(this.eligibilityForm);

    const auditHistoryPath = this.data.get("auditHistoryUrl");
    if (this.hasAuditHistoryTarget) {
      this.formAuditHistoryModule = new FormAuditHistoryModule({
        form: this.eligibilityForm,
        formSchema: this.eligibilityFormSchema,
        auditHistoryUrl: buildAuditHistoryJsonUrl(auditHistoryPath),
        auditHistoryTarget: this.auditHistoryTarget,
        changeSummaryTarget: this.hasChangeSummaryTarget
          ? this.changeSummaryTarget
          : null,
        stackStatus: this.data.get("status"),
      });

      this.formAuditHistoryModule.load();
      this.eligibilityForm.on("render", () => {
        this.formAuditHistoryModule?.attachQuestionHistoryIcons();
      });
    }

    if (this.EligibilityFormAgreementElem.checked) {
      this.formState.disableForm();
    } else {
      this.formState.enableForm();
    }
  }

  checkifAllImagesUploaded() {
    this.eligibilityForm.on("change", () => {
      const qcImagesElements = document.querySelectorAll(".qc_images");
      this.dataQuestionArray = Array.from(qcImagesElements).map((element) =>
        element.getAttribute("data-question"),
      );
    });
  }

  addCornerstoneToolsAndAnnotations(imageUrl) {
    // Activate Pan Tool on Middle Click Drag
    cornerstoneTools.addTool(PanTool);
    cornerstoneTools.setToolActive("Pan", { mouseButtonMask: MIDDLE_CLICK });

    // Activate WWWc tool on Left Click Drag
    cornerstoneTools.addTool(WwwcTool);
    cornerstoneTools.setToolActive("Wwwc", { mouseButtonMask: LEFT_CLICK });

    // Activate Zoom Mouse Wheel Tool
    cornerstoneTools.addTool(ZoomMouseWheelTool, {
      // Optional configuration
      configuration: {
        invert: false,
        preventZoomOutsideImage: false,
        minScale: 0.01,
        maxScale: 20.0,
      },
    });
    cornerstoneTools.setToolActive("ZoomMouseWheel", {});
    // Add Non Active tools
    cornerstoneTools.addTool(LengthTool);
    cornerstoneTools.addTool(ArrowAnnotateTool);
    cornerstoneTools.addTool(AngleTool);
    cornerstoneTools.addTool(RectangleRoiTool);
    cornerstoneTools.addTool(FreehandRoiTool);
    cornerstoneTools.addTool(EraserTool);
    cornerstoneTools.addTool(cornerstoneTools.ProbeTool);
    cornerstoneTools.addTool(EllipticalRoiTool, {
      configuration: {
        drawHandlesOnHover: false,
        hideHandlesIfMoving: true,
        renderDashed: false,
      },
    });
    // register cornerstone tools events
    // https://github.com/cornerstonejs/cornerstoneTools/blob/master/src/events.js
    this.viewerElement.addEventListener(
      "cornerstonetoolsmeasurementcompleted",
      (e) => {
        console.log(`------${e.detail.toolName}-----`);
        console.table(e.detail.measurementData);
      },
    );
    this.refreshTools();
  }

  imageLoader(imageUrl) {
    imageUrl = document.location.origin + "/" + imageUrl;
    this.imageTag.innerHTML = "";
    if (imageUrl) {
      let imageIds = [];
      let imageIdRoot = "wadouri:" + imageUrl;
      let stack = {};
      // http:
      if (imageUrl.includes(".dcm")) {
        cornerstoneWADOImageLoader.wadouri.dataSetCacheManager
          .load(imageUrl, cornerstoneWADOImageLoader.internal.xhrRequest)
          .then((dataSet) => {
            // dataset is now loaded, get the # of frames so we can build the array of imageIds
            //(0028,0008)	IS	Number of Frames is the tag that defines the number of frames as per dicomliberary which means this will not change
            let numFrames = dataSet.intString("x00280008");
            this.imageTagList.forEach((t) => {
              if (dataSet.intString(t.hex)) {
                this.imageTag.innerHTML += `<div>${t.label} : ${dataSet.intString(t.hex)}</div>`;
              }
            });
            if (!numFrames) {
              imageIds.push(imageIdRoot);
            } else {
              for (var i = 0; i < numFrames; i++) {
                imageIds.push(imageIdRoot + "?frame=" + i);
              }
            }
            stack = {
              currentImageIdIndex: 0,
              imageIds: imageIds,
            };
            if (imageIds.length > 2) {
              cornerstone.loadAndCacheImage(imageIds[0]).then(
                (image) => {
                  // now that we have an image frame in the cornerstone cache, we can decrement
                  // the reference count added by load() above when we loaded the metadata.  This way
                  // cornerstone will free all memory once all imageId's are removed from the cache
                  cornerstone.displayImage(this.viewerElement, image);
                  cornerstoneTools.addStackStateManager(this.viewerElement, [
                    "stack",
                  ]);
                  cornerstoneTools.addToolState(
                    this.viewerElement,
                    "stack",
                    stack,
                  );
                  stack.imageIds.forEach((imageId) => {
                    this.loadExistingAnnotationsForImage(imageId);
                  });
                  this.addCornerstoneToolsAndAnnotations(imageIds[0]);
                  cornerstoneTools.addTool(StackScrollMouseWheelTool);
                  cornerstoneTools.setToolActive("StackScrollMouseWheel", {});
                },
                function (err) {
                  console.log("loadAndCacheImage error", err);
                },
              );
            } else {
              cornerstone.loadAndCacheImage(imageUrl).then(
                (image) => {
                  this.addCornerstoneToolsAndAnnotations(imageUrl);
                },
                function (err) {
                  console.log("loadImage error", err);
                },
              );
            }
          });
      }
    }
  }

  loadExistingAnnotationsForImage(url) {
    if (this.annotation[url] && !this.annotationIsLoadedOnce[url]) {
      cornerstoneTools.globalImageIdSpecificToolStateManager.restoreImageIdToolState(
        url,
        this.annotation[url],
      );
      this.annotationIsLoadedOnce[url] = true;
      this.refreshTools();
    }
  }

  refreshTools() {
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
    ];
    tools.forEach((tool) => {
      cornerstoneTools.setToolActive(tool, { mouseButtonMask: LEFT_CLICK });
    });
  }

  readImages() {
    const images = document.querySelectorAll(".image");
    images.forEach((i) => {
      this.images.push({
        id: i.getAttribute("data-id"),
        url: i.getAttribute("data-url"),
        elem: i,
      });
    });
    // console.log(this.images);
  }

  loadImage(url, id) {
    url = document.location.origin + url;
    cornerstone.loadAndCacheImage(url).then((image) => {
      this.loadExistingAnnotationsForImage(url);
      cornerstone.displayImage(this.viewerElement, image);
      cornerstone.enable(this.viewerElement);
    });
  }

  changeImage(e, elem) {
    let target;
    if (e) target = e.target;
    else target = elem;
    // load new image
    this.imageLoader(target.dataset.url);

    // if the call came from click adjust the currentImage index
    if (e) {
      this.currentImage = target.dataset.idx;
      console.log(`current index seleted ${this.currentImage}`);
    }
    // remove all active borders
    const allImageBoxes = document.querySelectorAll(".image-box");
    allImageBoxes.forEach((i) => i.classList.remove("border-primary-500"));

    // active border around current image
    target.parentNode.classList.add("border-primary-500");
  }

  nextImage(e) {
    console.log(`current index: ${this.currentImage}`);
    const len = this.images.length;
    this.currentImage = Math.abs((this.currentImage + 1) % len);
    console.log(` after: ${this.currentImage}`);
    this.changeImage(null, this.images[this.currentImage].elem);
    e.preventDefault();
  }

  previousImage(e) {
    console.log(`current index: ${this.currentImage}`);
    const len = this.images.length;
    this.currentImage = Math.abs(this.currentImage + len - 1) % len;
    console.log(` after: ${this.currentImage}`);
    this.changeImage(null, this.images[this.currentImage].elem);
    e.preventDefault();
  }

  toggleTags(e) {
    this.imageTag.classList.toggle("hidden");
    e.preventDefault();
  }

  labelTool(e) {
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
    ];

    tools.forEach((tool) => {
      cornerstoneTools.setToolEnabled(tool, { mouseButtonMask: LEFT_CLICK });
    });

    this.viewerElement.addEventListener("cornerstonetoolsmousedown", (e) => {
      const coord = e.detail.currentPoints.canvas;
      const mouseBtn = e.detail.buttons;
      // if its a right click
      if (mouseBtn === 2) {
        tools.forEach((tool) => {
          const state = cornerstoneTools.getToolState(this.viewerElement, tool);
          if (state) {
            state.data.forEach((d) => {
              const isNear = cornerstoneTools
                .getToolForElement(this.viewerElement, tool)
                .__proto__.pointNearTool(this.viewerElement, d, coord);
              if (isNear)
                console.log(`${tool} with ${d.uuid} near : ${isNear}`);
              cornerstoneTools.setToolActive(tool, {
                mouseButtonMask: LEFT_CLICK,
              });
              // when near is detected see if we can have the annotation highlited green
            });
          }
        });
      }
    });
    e.preventDefault();
  }

  levelsTool(e) {
    cornerstoneTools.setToolActive("Wwwc", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  panTool(e) {
    cornerstoneTools.setToolActive("Pan", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  lengthTool(e) {
    cornerstoneTools.setToolActive("Length", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  arrowAnnotationTool(e) {
    cornerstoneTools.setToolActive("ArrowAnnotate", {
      mouseButtonMask: LEFT_CLICK,
    });
    e.preventDefault();
  }

  angleTool(e) {
    cornerstoneTools.setToolActive("Angle", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  rectangleTool(e) {
    cornerstoneTools.setToolActive("RectangleRoi", {
      mouseButtonMask: LEFT_CLICK,
    });
    e.preventDefault();
  }

  freehandTool(e) {
    cornerstoneTools.setToolActive("FreehandRoi", {
      mouseButtonMask: LEFT_CLICK,
    });
    e.preventDefault();
  }

  ellipseTool(e) {
    cornerstoneTools.setToolActive("EllipticalRoi", {
      mouseButtonMask: LEFT_CLICK,
    });
    e.preventDefault();
  }

  eraserTool(e) {
    cornerstoneTools.setToolActive("Eraser", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  submitGrading(e) {
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
    ];
    const annotations =
      cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();
    this.saveGrading(annotations);
    e.preventDefault();
  }

  saveGrading(annotations) {
    const method = this.data.get("mode") == "update" ? "PATCH" : "POST";
    fetch(this.eligibilityAnnotationSaveUrl, {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: method,
      body: JSON.stringify({
        eligibility: {
          comment: "Update",
          annotations: annotations,
          user_id: this.EligibilityFormUserId,
          stack_id: this.gradingFormStackId,
        },
      }),
    }).then((response) => {
      console.log(response);
    });
  }

  get url() {
    return this.data.get("url");
  }

  formCommit(e) {
    if (!this.lockSubmit(e)) return;

    if (!this.agreementTarget.checked) {
      alert("You must agree to terms");
      this.unlockSubmit();
      return;
    }

    this.notifications.showLoader();

    this.handleFormCommitAndSubmit("completed", true);
  }

  formSaveDraft(e) {
    if (!this.lockSubmit(e)) return;

    this.notifications?.showLoader?.();

    this.handleFormCommitAndSubmit("initiated", true, true);
  }

  formUpdate(e) {
    if (!this.lockSubmit(e)) return;

    this.notifications?.showLoader?.();

    this.handleFormCommitAndSubmit("editing");
  }

  handleFormCommitAndSubmit(status, shouldSubmit = false, draftStatus = false) {
    return this.eligibilityForm
      .submit()
      .then((submission) => {
        if (status === "completed") {
          const uploaded = this.formSubmissionModule.checkIfAllImagesUploaded(
            submission,
            this.dataQuestionArray,
          );

          if (!uploaded) {
            this.notifications?.error("Images not uploaded");
            return Promise.reject("validation_failed");
          }
        }

        this.formSubmissionModule.collectImagesFromSelector(
          ".attached_images",
          this.gradingImages,
        );

        this.defaultSaveAnnotation();
        return this.formSubmissionModule.submitForm(
          submission,
          this.data.get("mode") === "update" ? "PATCH" : "POST",
          null,
          null,
          draftStatus,
          "eligibility_form",
          status,
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.unlockSubmit();
        this.notifications?.hideLoader?.();
      });
  }

  lockSubmit(e) {
    e.preventDefault();

    if (this.isSubmitting) return false;

    this.isSubmitting = true;
    this.disableUI();

    return true;
  }

  unlockSubmit() {
    this.isSubmitting = false;
    this.enableUI();
  }

  disableUI() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add(
        "opacity-50",
        "pointer-events-none",
      );
    }
  }

  enableUI() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove(
        "opacity-50",
        "pointer-events-none",
      );
    }
  }

  defaultSaveAnnotation() {
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
    ];
    // const annotations = {};

    const annotations =
      cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();

    this.saveGrading({ ...annotations, ...this.annotation });
  }

  toggleImage() {
    if (this.cornerstoneImages.dataset.expanded === "1") {
      this.collapse_cornerstone();
    } else {
      this.expand_cornerstone();
    }
  }

  collapse_cornerstone() {
    const grading_tools =
      this.cornerstoneImages.querySelector(".grading_tools");
    this.cornerstoneImages.classList.add("col-span-1");
    this.cornerstoneImages.classList.remove("col-span-6");
    grading_tools.classList.add("hidden");
    this.viewerTarget.classList.add("hidden");
    this.cornerstoneImages.dataset.expanded = "0";
    this.cornerstoneIconTarget.innerHTML = `VIEWER`;
    this.cornerstoneIconTarget.classList.add("text-white");
    this.cornerstoneIconTarget.classList.add("p-1");
    this.cornerstoneIconTarget.classList.remove("float-right");
    this.largeForm();
  }

  expand_cornerstone() {
    const grading_tools =
      this.cornerstoneImages.querySelector(".grading_tools");
    this.cornerstoneImages.classList.add("col-span-6");
    this.cornerstoneImages.classList.remove("col-span-1");
    grading_tools.classList.remove("hidden");
    this.viewerTarget.classList.remove("hidden");
    this.cornerstoneImages.dataset.expanded = "1";
    this.cornerstoneIconTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="white">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
        `;
    this.cornerstoneIconTarget.classList.remove("text-white");
    this.cornerstoneIconTarget.classList.remove("p-1");
    this.cornerstoneIconTarget.classList.add("float-right");
    this.reverseFormChanges();
  }

  largeForm() {
    this.formSection.classList.add("col-span-10");
    this.formSection.classList.remove("col-span-5");
  }

  reverseFormChanges() {
    this.formSection.classList.add("col-span-5");
    this.formSection.classList.remove("col-span-10");
  }

  toggleSubmitArea() {
    var staticPanel = document.getElementById("expend_colapse");
    if (this.questionAreaTarget.classList.contains("hidden")) {
      this.questionAreaTarget.classList.remove("hidden");
      staticPanel.classList.add("fa-minus-square-o");
      staticPanel.classList.remove("fa-plus-square-o");
    } else {
      this.questionAreaTarget.classList.add("hidden");
      staticPanel.classList.add("fa-plus-square-o");
      staticPanel.classList.remove("fa-minus-square-o");
    }
  }

  hideLoader() {
    this.loadingImageTarget.classList.remove("visible");
  }

  appendImageWithQuestion(component, imageUrls, imagesArray) {
    const addedImages = new Set();
    // Create a div to hold the images
    const imageDiv = document.createElement("div");
    imageDiv.classList.add(
      "mt-1",
      "border",
      "rounded",
      "border-green-300",
      "flex",
      "gap-2",
    );
    // Iterate through the image URLs and create image elements
    for (const imageUrl of imageUrls) {
      for (let i = 0; i < imagesArray.length; i++) {
        let imageUrlParts = imagesArray[i].split("/");
        let imageName = imageUrlParts[imageUrlParts.length - 1];
        // Decode the URL containing %20
        let decodedImageUrl = decodeURIComponent(imageName);
        if (imageUrl == decodedImageUrl && !addedImages.has(decodedImageUrl)) {
          const imageElement = document.createElement("img");
          imageElement.setAttribute("src", imagesArray[i]);
          imageElement.setAttribute("alt", "Attachment Image");
          imageElement.setAttribute("width", "100");
          imageElement.setAttribute("height", "100");
          imageElement.style.cursor = "pointer";
          // Create the delete icon using an SVG image
          const deleteIcon = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          deleteIcon.setAttribute("xmlns", "http://www.w3.org/2000/svg");
          deleteIcon.setAttribute("width", "20");
          deleteIcon.setAttribute("height", "20");
          deleteIcon.setAttribute("viewBox", "0 0 24 24");
          deleteIcon.style.cursor = "pointer";
          deleteIcon.setAttribute("id", "deleteIconId");
          if (this.eligibilityForm._disabled == false) {
            // Add SVG path for the red bin icon
            deleteIcon.innerHTML = `
                        <title>Delete Attachment</title>
                        <path fill="#FF0000" d="M18 4h-4.18l-1.82-2H10L8.82 4H4v2h16V4zM6.36 8h11.28l1.23 10.96l.01.04V20H4v-1.96l.01-.04L5.64 8zM8 18h8v-2H8v2zm0-4h8v-6H8v6z"/>
                        `;
          }
          const stackId = this.gradingFormStackId;
          deleteIcon.addEventListener("click", () => {
            // Display a confirmation dialog
            const confirmation = confirm(
              "Are you sure you want to delete this image?",
            );
            if (confirmation) {
              imageElement.parentElement.remove();
              addedImages.delete(decodedImageUrl);
              // Get the image URL associated with the clicked delete icon
              const imageUrlToDelete = imageElement.src;
              const fileSegments = imageUrlToDelete.split("/");
              const fileName = fileSegments[fileSegments.length - 1];
              // Send a DELETE request to the server to delete the image
              fetch(`/delete_image`, {
                method: "post",
                headers: {
                  "Content-Type": "application/json; charset=utf-8",
                  "X-CSRF-Token": document.head
                    .querySelector('meta[name="csrf-token"]')
                    .getAttribute("content"),
                },
                body: JSON.stringify({
                  image_url: fileName,
                  stack_id: stackId,
                  image_array: component.key,
                }),
              })
                .then((response) => {
                  if (response.ok) {
                    // Image deleted successfully from storage
                    console.log("Image deleted successfully");
                    // Remove the corresponding image element from the DOM
                    window.location.reload();
                  } else {
                    // Failed to delete image
                    console.error("Failed to delete image");
                  }
                })
                .catch((error) => {
                  console.error("Error:", error);
                });
            }
          });
          // Create a container div for the image and delete icon
          const imageContainer = document.createElement("div");
          imageContainer.classList.add("image-container");
          imageContainer.appendChild(imageElement);
          imageContainer.appendChild(deleteIcon);
          imageElement.addEventListener("click", function () {
            const lightboxImage = document.getElementById("lightbox-image");
            lightboxImage.src = imagesArray[i];
            const lightboxContainer =
              document.getElementById("lightbox-container");
            lightboxContainer.style.display = "block";
          });
          const lightboxContainer =
            document.getElementById("lightbox-container");
          lightboxContainer.addEventListener("click", function () {
            lightboxContainer.style.display = "none";
          });
          // Append the image to the div
          imageDiv.appendChild(imageContainer);
          addedImages.add(decodedImageUrl);
        }
      }
    }
    // Append the image div to the question component's element
    if (imageDiv.innerHTML.trim() != "") {
      component.element.appendChild(imageDiv);
    }
  }

  appendSdvWithQuestionTitles(component, check_sdv, sdv_comments) {
    if (check_sdv === "true") {
      // Create a span element for the SVG icon with the tooltip
      const svgDive = document.createElement("span");
      svgDive.classList.add("has-tooltip");
      svgDive.innerHTML = `
          <?xml version="1.0" encoding="iso-8859-1"?>
            <!-- Uploaded to: SVG Repo, www.svgrepo.com, Generator: SVG Repo Mixer Tools -->
            <!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
            <svg fill="#007BFF" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
                width="16px" height="16px" viewBox="0 0 509.604 509.604"
                xml:space="preserve" style ="margin-top:3px">
                <g>
                    <g>
                        <path d="M34.262,333.282c8.119,6.75,14.793,15.223,14.143,20.988c-0.382,3.443-0.593,6.943-0.593,10.5
                            c0,52.393,41.3,94.861,92.24,94.861c6.292,0,12.431-0.65,18.37-1.885c10.002-2.074,21.812,1.941,28.888,9.793
                            c16.82,18.646,40.803,30.342,67.492,30.342c28.19,0,53.426-13.016,70.342-33.518c6.723-8.146,18.103-11.533,28.22-8.5
                            c8.166,2.447,16.811,3.768,25.751,3.768c50.939,0,92.24-42.477,92.24-94.861c0-5.861-0.535-11.59-1.549-17.145
                            c-1.712-9.371,2.85-21.047,10.471-28.363c18.025-17.289,29.328-41.883,29.328-69.242c0-29.787-13.368-56.323-34.263-73.698
                            c-8.118-6.751-14.793-15.224-14.143-20.99c0.383-3.442,0.593-6.942,0.593-10.5c0-52.393-41.301-94.86-92.24-94.86
                            c-6.292,0-12.431,0.65-18.369,1.884c-10.002,2.075-21.812-1.941-28.889-9.792c-16.82-18.647-40.803-30.342-67.492-30.342
                            c-26.688,0-50.671,11.695-67.492,30.342c-7.076,7.841-18.886,11.867-28.888,9.792c-5.938-1.234-12.078-1.884-18.37-1.884
                            c-50.939,0-92.24,42.477-92.24,94.86c0,5.049,0.392,10.002,1.147,14.832c1.262,8.128-4.447,18.149-12.747,24.681
                            C14.219,201.663,0,228.887,0,259.583C0,289.37,13.368,315.907,34.262,333.282z M131.475,263.016
                            c2.046-3.625,7.268-3.672,12.049,0.479l48.119,33.918c2.61,1.588,5.106,2.4,7.506,2.4c4.963,0,8.893-3.576,12.689-7.02
                            l153.985-154.138c9.629-10.471,18.99-14.162,25.102-10.146c2.82,1.855,4.646,4.647,5.135,7.87
                            c0.583,3.825-0.756,7.946-3.768,11.599l-185.149,224.91c-2.687,3.26-6.11,5.059-9.629,5.059c-4.179,0-7.965-2.516-10.404-6.895
                            l-54.344-97.969C130.519,269.422,130.021,265.618,131.475,263.016z"/>
                    </g>
                </g>
            </svg>     
            <span class="tooltip bottom-2 w-min">${sdv_comments}</span>
          `;
      // Get the label element within component.element
      const labelElement = component.element.querySelector("label");
      if (labelElement) {
        labelElement.classList.add("flex", "gap-2");
        // Concatenate component.label and svgDive and set it as the innerHTML of labelElement
        labelElement.innerHTML = component.label + svgDive.outerHTML;
      } else {
        console.error("Label element not found.");
      }
    }
  }

  printForm(event) {
    event.preventDefault();
    const auditHistoryPath = this.data.get("auditHistoryUrl");
    const auditPromise =
      this.printFormWithData &&
      (this.includeAuditSummaryInPdf || this.includeChangeSummaryInPdf) &&
      auditHistoryPath &&
      shouldShowAuditHistory(this.data.get("status"))
        ? fetchAuditHistory(auditHistoryPath)
        : Promise.resolve(null);

    Promise.all([this.getFormData(this.gradingFormStackId), auditPromise])
      .then(([response, auditHistory]) => {
        const eligibilityDetails = response.eligibility_details;

        // Create the first table for eligibility details
        const customTable = document.createElement("table");
        customTable.classList.add("custom-table");
        customTable.style.width = "100%";
        customTable.style.margin = "0 auto";
        customTable.style.borderCollapse = "collapse";
        customTable.style.boxShadow = "0px 4px 8px rgba(0, 0, 0, 0.1)";
        customTable.style.borderRadius = "8px";
        customTable.style.overflow = "hidden";
        customTable.style.marginBottom = "20px";
        customTable.style.fontFamily = "Calibri, sans-serif";

        const headerDataPairs = [
          { header: "Site", key: "site_number" },
          { header: "Subject ID", key: "subject_code" },
          { header: "Visit Name", key: "visit_name" },
          { header: "Visit Date", key: "visit_date" },
        ];

        // Loop to create table rows for each header-data pair
        for (let i = 0; i < headerDataPairs.length; i += 4) {
          const row = customTable.insertRow();

          for (let j = i; j < i + 4 && j < headerDataPairs.length; j++) {
            const pair = headerDataPairs[j];
            const headerCell = row.insertCell();
            headerCell.textContent = pair.header;
            headerCell.style.fontWeight = "bold";
            headerCell.style.padding = "12px";
            headerCell.style.borderBottom = "1px solid #ddd";

            const dataCell = row.insertCell();
            // If printFormWithData is false, show dashes instead of actual data
            dataCell.textContent = this.printFormWithData
              ? eligibilityDetails[pair.key]
              : "-";
            dataCell.style.padding = "12px";
            dataCell.style.borderBottom = "1px solid #ddd";
            dataCell.style.textAlign = "center"; // Center the data
          }
        }
        // Use original schema if not printing with data, otherwise use current schema
        const schemaToUse = !this.printFormWithData
          ? this.originalEligibilityFormSchema
          : this.eligibilityFormSchema;
        // Include all components - make it generic for all studies
        const updatedComponents = schemaToUse.components || [];

        // Use empty responses if not printing with data
        const responsesToUse = !this.printFormWithData
          ? { data: {} }
          : this.eligibilityFormResponses;

        const keys = updatedComponents.map((component) => component.title);
        // Create the second table for keys
        const keysTable = document.createElement("table");
        keysTable.classList.add("keys-table");
        keysTable.style.marginTop = "10px";
        keysTable.style.width = "100%";
        keysTable.style.margin = "0 auto";
        keysTable.style.borderCollapse = "collapse";
        keysTable.style.boxShadow = "0px 4px 8px rgba(0, 0, 0, 0.1)";
        keysTable.style.borderRadius = "8px";
        keysTable.style.overflow = "hidden";
        keysTable.style.fontFamily = "Calibri, sans-serif";
        keysTable.style.fontSize = "15px";

        const keysHeaderRow = keysTable.insertRow();
        ["EYE", "OD", "OS"].forEach((text) => {
          const cell = keysHeaderRow.insertCell();
          cell.textContent = text;
          cell.style.fontWeight = "bold";
          cell.style.color = "white";
          cell.style.backgroundColor = "#17365d";
          cell.style.textAlign = "center";
          cell.style.borderBottom = "2px solid #ddd";

          // Adjust column widths for OD and OS
          if (text === "OD" || text === "OS") {
            cell.style.width = "30%";
          } else {
            cell.style.width = "40%";
          }
        });
        // Helper function to create the question cell
        function createQuestionCell(subComponent, questionNumber) {
          const originalLabel = subComponent.label || "";

          // Check if original label had a number pattern
          const hadNumber = /^\d+[\)\.]\s*/.test(originalLabel);

          // Remove existing question numbers (like "2)", "5)", etc.) and OD/OS suffixes
          let cleanedLabel = originalLabel
            .replace(/^\d+\)\s*/, "") // Remove leading numbers like "2) "
            .replace(/^\d+\.\s*/, "") // Remove leading numbers like "2. "
            .replace(/- (OD|OS)$/, "") // Remove "- OD" or "- OS" suffix
            .trim();

          const description = subComponent.description
            ? `<div style="font-size: 12px; color: #666; margin-top: 5px; font-family: Calibri, sans-serif;">${subComponent.description}</div>`
            : ""; // Include description only if present

          // Only add question number if the original label had a number
          const questionPrefix = hadNumber ? `${questionNumber}) ` : "";

          return `
                        <div style="font-weight: bold; font-size: 15px; margin-bottom: 5px; font-family: Calibri, sans-serif;">
                            ${questionPrefix}${cleanedLabel}
                        </div>
                        ${description}
                    `;
        }

        // Generic rendering for all studies - process all components
        keys.forEach((label, index) => {
          const component = updatedComponents[index];
          if (!component || !component.components) return;

          // Track question number for this component/panel - restart numbering for each section
          let questionNumber = 1;

          // Create a full-width row for the main label (header row for component)
          const labelRow = keysTable.insertRow();
          const labelCell = labelRow.insertCell();
          labelCell.colSpan = 3; // Span 3 columns for full width
          // Remove "OD" or "OS" suffix from the label if present
          const cleanedLabel = label.replace(/\s*(OD|OS)$/, "").trim();
          labelCell.innerHTML = `
                        <div style="font-weight: bold; font-size: 18px; margin: 10px 0; text-align: center; background: #eaf1dd; padding: 10px; border-radius: 5px; font-family: Calibri, sans-serif;">
                            ${cleanedLabel || label}
                        </div>
                    `;
          labelCell.style.textAlign = "center";
          labelCell.style.borderBottom = "2px solid #ddd";

          // Iterate through subcomponents
          component.components.forEach((subComponent) => {
            if (subComponent.type === "htmlelement") {
              // Add a single row with full-width content for htmlelement
              const htmlRow = keysTable.insertRow();
              const htmlCell = htmlRow.insertCell();
              htmlCell.colSpan = 3;
              htmlCell.innerHTML = `
                                <div style="font-weight: bold; font-size: 14px; margin: 5px 0; text-align: center; background: #eaf1dd; padding: 2px; border-radius: 5px; line-height: 1.0; font-family: Calibri, sans-serif;">
                                    ${subComponent.content}
                                </div>
                            `;
              htmlCell.style.textAlign = "center";
              htmlCell.style.borderBottom = "1px solid #ddd";
            } else {
              // Process questions that have OD/OS structure
              // Check if this is an OD question or if there's a corresponding OS question
              const isODQuestion =
                subComponent.key.endsWith("OD") ||
                subComponent.key.endsWith("_OD");
              const isOSQuestion =
                subComponent.key.endsWith("OS") ||
                subComponent.key.endsWith("_OS");

              // Generate OS key - try multiple patterns
              let osKey = null;
              if (isODQuestion) {
                // Try different replacement patterns
                if (subComponent.key.endsWith("_OD")) {
                  osKey = subComponent.key.replace("_OD", "_OS");
                } else if (subComponent.key.endsWith("OD")) {
                  osKey = subComponent.key.replace(/OD$/, "OS");
                }
              }

              const odKey = isOSQuestion
                ? subComponent.key.endsWith("_OS")
                  ? subComponent.key.replace("_OS", "_OD")
                  : subComponent.key.replace(/OS$/, "OD")
                : null;

              // Search for OS question across ALL components, not just current one
              let osSubComponent = null;
              let hasOSQuestion = false;
              if (osKey) {
                // First check in current component
                osSubComponent = component.components.find(
                  (comp) => comp.key === osKey,
                );
                hasOSQuestion = !!osSubComponent;

                // If not found, search across all components
                if (!osSubComponent) {
                  updatedComponents.forEach((comp) => {
                    if (comp.components && !osSubComponent) {
                      const found = comp.components.find(
                        (sub) => sub.key === osKey,
                      );
                      if (found) {
                        osSubComponent = found;
                        hasOSQuestion = true;
                      }
                    }
                  });
                }

                // If still not found, try case-insensitive search and pattern matching
                if (!osSubComponent) {
                  updatedComponents.forEach((comp) => {
                    if (comp.components && !osSubComponent) {
                      const found = comp.components.find((sub) => {
                        // Exact match (case-insensitive)
                        if (sub.key.toLowerCase() === osKey.toLowerCase())
                          return true;
                        // Pattern match: ends with OS and base matches
                        if (sub.key.toLowerCase().endsWith("os")) {
                          const baseKey = sub.key.toLowerCase().slice(0, -2);
                          const expectedBase = osKey.toLowerCase().slice(0, -2);
                          if (baseKey === expectedBase) return true;
                        }
                        // Try matching by label if key doesn't match but label suggests it's the OS version
                        if (subComponent.label && sub.label) {
                          const odLabel = subComponent.label
                            .replace(/\s*[-–]\s*(OD|os)$/i, "")
                            .trim();
                          const osLabel = sub.label
                            .replace(/\s*[-–]\s*(OS|od)$/i, "")
                            .trim();
                          if (
                            odLabel === osLabel &&
                            sub.key.toLowerCase().includes("os")
                          ) {
                            return true;
                          }
                        }
                        return false;
                      });
                      if (found) {
                        osSubComponent = found;
                        hasOSQuestion = true;
                      }
                    }
                  });
                }
              }

              const hasODQuestion =
                odKey &&
                component.components.some((comp) => comp.key === odKey);

              // Process OD questions (they will handle their OS pair), or standalone questions without OD/OS pairs
              // Skip OS questions that have a corresponding OD (to avoid duplicates)
              if (isODQuestion || (!isOSQuestion && !hasODQuestion)) {
                // Create a row for each question
                const row = keysTable.insertRow();

                // First column: Question text with question number (if original had number)
                const questionCell = row.insertCell();
                const originalLabel = subComponent.label || "";
                const hadNumber = /^\d+[\)\.]\s*/.test(originalLabel);

                questionCell.innerHTML = createQuestionCell(
                  subComponent,
                  questionNumber,
                );
                questionCell.style.textAlign = "left";
                questionCell.style.borderBottom = "1px solid #ddd";
                questionCell.style.padding = "8px";
                questionCell.style.verticalAlign = "top";

                // Increment question number only if original had a number
                if (hadNumber) {
                  questionNumber++;
                }

                if (isODQuestion && hasOSQuestion && osSubComponent) {
                  // Has both OD and OS - show in separate columns
                  // Second column: OD input field
                  const odFieldCell = row.insertCell();
                  const odValue = responsesToUse.data
                    ? responsesToUse.data[subComponent.key] || ""
                    : "";
                  odFieldCell.innerHTML = renderField(subComponent, odValue);
                  odFieldCell.style.textAlign = "left";
                  odFieldCell.style.borderBottom = "1px solid #ddd";
                  odFieldCell.style.padding = "8px";
                  odFieldCell.style.verticalAlign = "top";
                  odFieldCell.style.wordWrap = "break-word";

                  // Third column: OS input field
                  const osFieldCell = row.insertCell();
                  const osValue = responsesToUse.data
                    ? responsesToUse.data[osKey] || ""
                    : "";
                  // Always render the field, even if empty, so options are shown
                  osFieldCell.innerHTML = renderField(osSubComponent, osValue);
                  osFieldCell.style.textAlign = "left";
                  osFieldCell.style.borderBottom = "1px solid #ddd";
                  osFieldCell.style.padding = "8px";
                  osFieldCell.style.verticalAlign = "top";
                  osFieldCell.style.wordWrap = "break-word";
                } else if (isODQuestion && !hasOSQuestion) {
                  // OD question exists but OS not found - still show OD, but indicate OS missing
                  const odFieldCell = row.insertCell();
                  const odValue = responsesToUse.data
                    ? responsesToUse.data[subComponent.key] || ""
                    : "";
                  odFieldCell.innerHTML = renderField(subComponent, odValue);
                  odFieldCell.style.textAlign = "left";
                  odFieldCell.style.borderBottom = "1px solid #ddd";
                  odFieldCell.style.padding = "8px";
                  odFieldCell.style.verticalAlign = "top";
                  odFieldCell.style.wordWrap = "break-word";

                  // OS column - show not found message
                  const osFieldCell = row.insertCell();
                  osFieldCell.innerHTML =
                    "<div style='color: #999; font-style: italic; font-size: 15px; font-family: Calibri, sans-serif; padding: 5px;'>(OS QUESTION NOT FOUND: " +
                    osKey +
                    ")</div>";
                  osFieldCell.style.textAlign = "left";
                  osFieldCell.style.borderBottom = "1px solid #ddd";
                  osFieldCell.style.padding = "8px";
                  osFieldCell.style.verticalAlign = "top";
                } else {
                  // Single question without OD/OS structure - span both columns
                  const combinedCell = row.insertCell();
                  combinedCell.colSpan = 2;
                  const value = responsesToUse.data
                    ? responsesToUse.data[subComponent.key] || ""
                    : "";
                  combinedCell.innerHTML = renderField(subComponent, value);
                  combinedCell.style.textAlign = "left";
                  combinedCell.style.borderBottom = "1px solid #ddd";
                  combinedCell.style.padding = "8px";
                  combinedCell.style.verticalAlign = "top";
                  combinedCell.style.wordWrap = "break-word";
                }
              }
            }
          });
        });

        /**
         * Helper function to render fields based on their type
         */
        function renderField(subComponent, value) {
          // Check if value is empty for all field types
          const isEmpty =
            value === null ||
            value === undefined ||
            value === "" ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === "object" && Object.keys(value).length === 0);

          if (
            subComponent.type === "radio" &&
            Array.isArray(subComponent.values)
          ) {
            // Render radio buttons
            const radioOptions = subComponent.values
              .map(
                (opt) => `
                                <div style="margin-top: 5px; text-align: left;">
                                    <input type="radio" 
                                        id="${subComponent.key}_${opt.value}" 
                                        name="${subComponent.key}" 
                                        value="${opt.value}" 
                                        style="margin-right: 5px;" 
                                        ${value == opt.value ? 'checked="checked"' : ""} 
                                        disabled="disabled" />
                                    <label for="${subComponent.key}_${opt.value}" 
                                        style="font-size: 15px; font-family: Calibri, sans-serif; ${value == opt.value ? "font-weight: bold; color: #333;" : ""}">
                                        ${(opt.label || "").toUpperCase()}
                                    </label>
                                </div>
                            `,
              )
              .join("");

            // Always show options
            return radioOptions;
          } else if (
            subComponent.type === "checkbox" &&
            Array.isArray(subComponent.values)
          ) {
            // Render checkboxes
            const checkboxOptions = subComponent.values
              .map(
                (opt) => `
                                <div style="margin-top: 5px; text-align: left;">
                                    <input type="checkbox" 
                                        id="${subComponent.key}_${opt.value}" 
                                        name="${subComponent.key}[]" 
                                        value="${opt.value}" 
                                        style="margin-right: 5px;" 
                                        ${value && value.includes(opt.value) ? 'checked="checked"' : ""} 
                                        disabled="disabled" />
                                    <label for="${subComponent.key}_${opt.value}" 
                                        style="font-size: 15px; font-family: Calibri, sans-serif; ${value && value.includes(opt.value) ? "font-weight: bold; color: #ff0000;" : ""}">
                                        ${(opt.label || "").toUpperCase()}
                                    </label>
                                </div>
                            `,
              )
              .join("");

            // Always show options
            return checkboxOptions;
          } else if (subComponent.type === "htmlelement") {
            return "";
          } else {
            // Render text input for other types - use textarea for better text wrapping
            const displayValue = value || "";
            return `
                            <div style="margin-bottom: 8px; width: 100%;">
                                <textarea 
                                    id="${subComponent.key}" 
                                    name="${subComponent.key}" 
                                    rows="${Math.max(2, Math.ceil((displayValue.length || 1) / 50))}"
                                    style="width: 95%; font-size: 15px; font-family: Calibri, sans-serif; padding: 5px; margin-top: 5px; border: 1px solid #ddd; resize: vertical; word-wrap: break-word; overflow-wrap: break-word; color: #333;" 
                                    disabled="disabled">${displayValue}</textarea>
                            </div>
                        `;
          }
        }
        // Define headers and keys for the third table
        const thirdTableData = [{ header: "Signature", value: "" }];

        // Create the third table
        const thirdTable = document.createElement("table");
        thirdTable.classList.add("third-table");
        thirdTable.style.width = "100%";
        thirdTable.style.borderCollapse = "collapse";
        // thirdTable.style.border = '1px solid #ddd'; // Add border for the table
        thirdTable.style.borderRadius = "8px";
        thirdTable.style.marginBottom = "20px";
        thirdTable.style.fontFamily = "Calibri, sans-serif";
        thirdTable.style.fontSize = "16px";

        // Populate the third table
        thirdTableData.forEach((item) => {
          const row = thirdTable.insertRow();

          // Header cell
          const headerCell = row.insertCell();
          headerCell.textContent = item.header;
          headerCell.style.fontWeight = "bold";
          headerCell.style.padding = "12px";
        });
        const studyName = eligibilityDetails.study_name;

        // Open a new window and add the tables
        const newWindow = window.open("", "_blank");

        // Add content to the new window
        newWindow.document.write(`
                                 <!DOCTYPE html>
                                 <html lang="en">
                                 <head>
                                     <meta charset="UTF-8">
                                     <meta name="viewport" content="width=device-width, initial-scale=1.0">
                                     <title>ELIGIBILITY FORM</title>
                                     <style>
                                         .custom-table, .keys-table {
                                             width: 100%;
                                             border-collapse: collapse;
                                             margin-top: 20px;
                                         }
                                         .custom-table th, .custom-table td,
                                         .keys-table th, .keys-table td {
                                             border: 1px solid #ddd;
                                             padding: 2px;
                                             text-align: center;
                                         }
                                         .custom-header {
                                             font-size: 16px;
                                             font-weight: bold;
                                             margin-bottom: 20px;
                                             text-align: center;
                                             color: #17365d;
                                             width: 100%;
                                             font-family: Calibri, sans-serif;
                                         }
                                         .header-container {
                                             display: flex;
                                             align-items: center;
                                             justify-content: space-between;
                                             margin-bottom: 20px;
                                             position: relative;
                                             padding: 0 20px;
                                             box-sizing: border-box;
                                         }
                                         .left-text {
                                             font-size: 18px;
                                             color: #17365d;
                                             font-weight: bold;
                                             font-family: Calibri, sans-serif;
                                             flex: 0 0 auto;
                                             min-width: 200px;
                                             max-width: 25%;
                                             padding-right: 15px;
                                             line-height: 1.3;
                                             white-space: normal;
                                         }
                                         .study-title {
                                             position: absolute;
                                             left: 50%;
                                             transform: translateX(-50%);
                                             font-size: 22px;
                                             font-weight: bold;
                                             text-align: center;
                                             color: #17365d;
                                             font-family: Calibri, sans-serif;
                                             max-width: 55%;
                                             line-height: 1.3;
                                             padding: 0 15px;
                                             box-sizing: border-box;
                                             word-wrap: normal;
                                             overflow-wrap: normal;
                                             white-space: normal;
                                         }
                                         .logo {
                                             width: 200px;
                                             height: auto;
                                             flex: 0 0 auto;
                                             margin-left: auto;
                                         }
                                         @media print {
                                             .header-container {
                                                 padding: 0 15px;
                                             }
                                             .study-title {
                                                 max-width: 50%;
                                                 font-size: 16px;
                                                 padding: 0 25px;
                                                 line-height: 1.3;
                                             }
                                             .logo {
                                                 width: 160px;
                                             }
                                             .left-text {
                                                 font-size: 15px;
                                                 min-width: 180px;
                                                 max-width: 22%;
                                                 padding-right: 20px;
                                                 line-height: 1.3;
                                             }
                                         }
                                     </style>
                                 </head>
                                 <body>
                                     <div class="header-container">
                                         <div class="left-text">OCULAR IMAGING <br> RESEARCH & <br> READING CENTER</div>
                                         <div class="study-title">${studyName}</div>
                                         <img src="${document.querySelector('meta[name="asset-base-path"]').getAttribute("content")}" alt="Logo" class="logo" />
                                     </div>
                                     <div class="custom-header">ELIGIBILITY FORM</div>
                                 </body>
                                 </html>
                             `);

        // Ensure the new window is ready before appending content
        newWindow.document.body.appendChild(customTable);
        newWindow.document.body.appendChild(keysTable);
        newWindow.document.body.appendChild(thirdTable);

        if (
          this.includeChangeSummaryInPdf &&
          auditHistory?.change_summary?.has_changes
        ) {
          const changeSummarySection = document.createElement("div");
          changeSummarySection.innerHTML =
            buildChangeSummaryPrintHtml(auditHistory);
          if (changeSummarySection.innerHTML.trim()) {
            newWindow.document.body.appendChild(changeSummarySection);
          }
        }

        if (this.includeAuditSummaryInPdf && auditHistory?.versions?.length) {
          const auditSection = document.createElement("div");
          auditSection.innerHTML = buildAuditHistoryPrintHtml(
            auditHistory,
            document.location.origin,
          );
          newWindow.document.body.appendChild(auditSection);
        }

        // Close the document to render content
        newWindow.document.close();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  getFormData(stackId) {
    const url = `${document.location.origin}/studies/${this.eligibilitystudyId}/eligibility_forms/get_eligibility_form_details`;
    return fetch(url, {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: "POST",
      body: JSON.stringify({
        stack_id: stackId,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }
        // Parse JSON response
        return response.json();
      })
      .then((data) => {
        // Handle the data received from the server
        return data; // Return the data if necessary
      })
      .catch((error) => {
        console.error(error);
        // Handle errors if any
      });
  }
}
